import React, { JSX, useContext, useEffect, useState } from "react";
import { BenchmarkingQHPService } from "@app/overviews/network-benchmarking-dashboard";
import { BenchmarkingFiltersContext, BenchmarkingServiceAreaFilters, NetworkFlyoutCountiesTable, NetworkFlyoutPlansTable } from "@app/overviews/network-benchmarking-dashboard/filters";
import { QHPContractPrimaryFiltersPayload, QHPFiltersContext, QHPNetwork } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters";
import { Accordion, AccordionContainer, LoadingSpinner } from "@app/shared";
import { Roles, UserContext } from "@app/user";
import { Link } from "react-router-dom";

interface QHPNetworkFlyoutProps {
  contract: QHPNetwork;
}

const QHPNetworkFlyout = (props: QHPNetworkFlyoutProps): JSX.Element => {
  const [contract, setContract] = useState<QHPNetwork>(null);
  const [isYourNetwork, setIsYourNetwork] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const qhpFiltersContext = useContext(QHPFiltersContext);
  const userContext = useContext(UserContext);

  const benchmarkingQHPService = new BenchmarkingQHPService();

  useEffect(() => {
    if (!!props.contract?.contractId) {
      const yourNetworks: QHPNetwork[] = qhpFiltersContext.parentOrganizations.find(x => x.parentOrganizationName === "Your QES Networks")?.hContracts;
      setIsYourNetwork((yourNetworks.map(x => x.contractId).includes(props.contract?.contractId)));
    }
  }, [props.contract?.contractId]);

  useEffect(() => {
    if (!!props.contract?.contractId && !!benchmarkingFiltersContext.displayedPrimaryFilters) {
      getContractDetails();
    }
  }, [props.contract?.contractId, benchmarkingFiltersContext.displayedPrimaryFilters]);

  const getContractDetails = (): void => {
    setLoading(true);

    const contractPayload: QHPContractPrimaryFiltersPayload = benchmarkingFiltersContext.displayedPrimaryFilters as QHPContractPrimaryFiltersPayload;
    contractPayload.versionId = props.contract.versionId;

    if (contractPayload.starRatings.length === qhpFiltersContext.mappedStarRatings.length) {
      contractPayload.starRatings = [];
    }

    benchmarkingQHPService.getContractDetails(contractPayload).then((data) => {
      data.parentOrganization = props.contract.parentOrganization;
      data.disabled = props.contract.disabled;
      setContract(data);
      setLoading(false);
    });
  }

  return (
    <React.Fragment>
      <div className="row">
        <div className="col-md-12">
          <div>
            <span className="font-weight-bold">Payer Name:</span> {props.contract?.parentOrganization}
          </div>
          <div>
            <span className="font-weight-bold">Issuer ID:</span> {props.contract?.issuerId}
          </div>
          <div>
            <span className="font-weight-bold">Network Name:</span> {props.contract?.networkName}
          </div>
        </div>
      </div>

      <BenchmarkingServiceAreaFilters />

      <div className="row">
        <div className="col-12">
          {loading ? (
            <div className="text-center">
              <LoadingSpinner />
            </div>
          ) : (
            <AccordionContainer>
              <Accordion
                title={`${!isYourNetwork ? "Active " : ""}Counties`}
                testId="h-contract-flyout__counties-accordion"
              >
                <NetworkFlyoutCountiesTable
                  contract={contract}
                  data={contract?.counties}
                  isYourNetwork={isYourNetwork}
                  loading={loading}
                  onApplyServiceAreaClick={qhpFiltersContext.onApplyServiceAreaClick}
                />
              </Accordion>

              <Accordion
                title="Plans"
                testId="h-contract-flyout__plans-accordion"
              >
                <NetworkFlyoutPlansTable
                  contractId={contract?.contractId}
                  data={contract?.plans}
                />
              </Accordion>

              {(userContext.roles.includes(Roles.CLIENT_ADMIN) || isYourNetwork) && (
                <Accordion
                  title="Data Details"
                  testId="h-contract-flyout__data-details"
                >
                  <div className="row">
                    <div className="col-md-12">
                      <label className="label-m">
                        QES Data
                      </label>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-3">
                      <label className="font-weight-bold">
                        QES Project Id:
                      </label>
                    </div>
                    <div className="col-md-9">
                      {!!props.contract?.projectId ?
                        <Link to={`/projects/${props.contract.projectId}`} target="_blank">{props.contract.projectId}</Link> :
                        "---"
                      }
                    </div>
                  </div>
                </Accordion>
              )}
            </AccordionContainer>
          )}
        </div>
      </div>
    </React.Fragment>
  );
}

export default QHPNetworkFlyout;
